import { builder } from '@builder.io/sdk';
import React, { useEffect, useRef } from 'react';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { getPromotions } from '@/services/promo.service';
import useStore from '@/store/useStore';

import Layout from '@/components/Layout/Layout';
import Breadcrumbs from '@/components/BuilderIO/common/Breadcrumbs/Breadcrumbs';
import SlickCarousel from '@/components/Carousel/SlickCarousel';

import data from '@/data/mission/about-us';

import MissionHeroBanner from '@/components/mission/MissionHeroBanner/MissionHeroBanner';
import LargeCopySection from '@/components/mission/LargeCopySection/LargeCopySection';
import ContentBlockLite from '@/components/mission/ContentBlockLite/ContentBlockLite';
import MissionNavTop from '@/components/mission/MissionNavTop';
import StatSection from '@/components/mission/StatSection';
import ContentBlockSection from '@/components/mission/ContentBlockSection/ContentBlockSection';
import MissionGridSection from '@/components/mission/MissionGridSection/MissionGridSection';
import MissionNav from '@/components/mission/MissionNav';

import useStaticPageSetData from '@/hooks/useStaticPageSetData';
import { DeviceType } from '@/utils/utils';
import { getRefsMaxHeight, resetRefsHeight, setRefsSameHeight } from '@/utils/builderUtils';
import { renderMobileNavShellHtml, renderNavHtml } from '@/widgets/navLinks/renderNavHtml';

const { NEXT_PUBLIC_BUILDER_API_KEY } = getConfig().publicRuntimeConfig;

builder.init(NEXT_PUBLIC_BUILDER_API_KEY);
const HeaderDynamic = dynamic(() => import('@/components/Header/Header'));
const MobileHeaderDynamic = dynamic(() => import('@/components/Header/components/MobileHeader'));

export async function getServerSideProps({ locale, req }: GetServerSidePropsContext) {
  const device = req?.headers['device_type'] as DeviceType;
  const formattedLocale = locale!.substring(0, 2) + locale!.substring(2, 5).toUpperCase();
  const navLink = 'nav-links';

  // call data in parallel to serve response faster
  const builderData = await Promise.allSettled([
    builder.getAll(navLink),
    builder.getAll('footer-links-desktop'),
    builder.getAll('footer-links-mobile'),
    getPromotions(),
    builder
      .get('header-banner', {
        userAttributes: {
          locale: formattedLocale,
        },
      })
      .toPromise(),
    // eslint-disable-next-line @typescript-eslint/no-shadow
    builder.getAll('mobile-nav-links').then(item => item.map(({ data }) => data)),
  ]);

  const navLinks = builderData[0].status === 'fulfilled' ? builderData[0].value : [];
  const footerLinks = builderData[1].status === 'fulfilled' ? builderData[1].value : [];
  const mobileFooterLinks = builderData[2].status === 'fulfilled' ? builderData[2].value : [];
  const promotions = builderData[3].status === 'fulfilled' ? builderData[3].value : {};
  const headerBanner = builderData[4].status === 'fulfilled' ? builderData[4].value : [];
  const mobileNavLinks = builderData[5].status === 'fulfilled' ? builderData[5].value : [];
  const desktopNavHtml = renderNavHtml({
    navLinks: navLinks.map(l => l.data),
    mobileNavLinks: [],
    promo: (promotions as any).topNav ?? null,
  }).desktopHtml;
  const mobileNavHtml = renderMobileNavShellHtml({ mobileNavLinks });

  return {
    props: {
      pageData: {
        header: {
          navLinks: navLinks.map(l => l.data),
          navHtml: desktopNavHtml,
          mobileNavHtml,
        },
        footer: {
          links: footerLinks.map(l => l.data),
          mobileLinks: mobileFooterLinks.map(l => l.data),
        },
        ...data,
        locale,
        promotions,
        headerBanner,
        mobileNavLinks,
        device,
      },
    },
  };
}

export default function Page({ pageData }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  useStaticPageSetData(pageData);
  const builderData = useStore(state => state.builderData);
  const setMobileNavLinks = useStore(state => state.setMobileNavLinks);
  const HeaderRefs = useRef<any[]>([]);
  const DescriptionRefs = useRef<any[]>([]);

  useEffect(() => {
    setMobileNavLinks(pageData.mobileNavLinks);
  }, []);

  useEffect(() => {
    if (HeaderRefs.current.length === 0 || DescriptionRefs.current.length === 0) return;
    const resizeHeight = () => {
      resetRefsHeight(HeaderRefs, 'auto');
      resetRefsHeight(DescriptionRefs, 'auto');
      if (window.innerWidth < 768) return;
      const headersMaxHeight = getRefsMaxHeight(HeaderRefs);
      const descriptionsMaxHeight = getRefsMaxHeight(DescriptionRefs);
      setRefsSameHeight(HeaderRefs, headersMaxHeight);
      setRefsSameHeight(DescriptionRefs, descriptionsMaxHeight);
    };
    resizeHeight();
    window.addEventListener('resize', resizeHeight);
    return () => {
      window.removeEventListener('resize', resizeHeight);
    };
  }, []);

  return (
    <Layout
      page={pageData.layout}
      footerLinks={pageData.footer.links}
      mobileFooterLinks={pageData.footer.mobileLinks}
      builderData={builderData}
    >
      <MobileHeaderDynamic
        className="lg:tw-hidden md:tw-hidden"
        mobileNavLinks={pageData.mobileNavLinks}
        mobileNavHtml={pageData.header.mobileNavHtml}
      />
      <HeaderDynamic
        className="tw-hidden md:tw-block lg:tw-block"
        navLinks={pageData.header.navLinks}
        navHtml={pageData.header.navHtml}
      />

      <div className="tw-mb-6 lg:tw-mb-0">
        <Breadcrumbs currentPageName={pageData.breadcrumbs.pageName} crumbs={pageData.breadcrumbs.crumbs} />
      </div>

      <div id="component-container">
        <MissionHeroBanner
          {...data.hero}
          title={
            <>
              Delight in
              <br className="lg:tw-hidden" /> the Details
            </>
          }
          autoPlay
          loop
          isHeader
          hasIcon
        />
        <MissionNavTop links={data.missionNavLinks} />

        <ContentBlockLite {...data.contentBlock1} />

        <LargeCopySection {...data.largeCopySection1} />

        <div className="tw--mt-[1px]">
          <StatSection {...data.statSection1} type="oneByFour" color="dark-green" textPosition="left" />
        </div>

        <ContentBlockSection backgroundColor="grey-20" header="The Brilliant Earth Difference">
          <ContentBlockLite {...data.contentBlockSlide1} titleClassName="tw-text-[28px]" />
          <ContentBlockLite {...data.contentBlockSlide2} titleClassName="tw-text-[28px]" />
          <ContentBlockLite {...data.contentBlockSlide3} titleClassName="tw-text-[28px]" />
        </ContentBlockSection>

        <ContentBlockSection
          backgroundColor="white"
          header="The Brilliant Earth Experience"
          classNames="tw-z-10 tw-relative"
        >
          <ContentBlockLite {...data.contentBlock2} />
        </ContentBlockSection>

        <MissionGridSection backgroundColor="dark-green" mobileColumns={1} tabletColumns={5} desktopColums={5}>
          {data.missionGridSection.map((missionGridData, index) => (
            <div
              className="tw-flex tw-flex-col tw-items-center tw-text-center tw-gap-4 tw-flex-[1_0_0] tw-self-stretch tw-text-white tw-h-full"
              key={`${missionGridData.title}_${index}`}
            >
              <h4
                className="tw-my-0 tw-text-be-lg tw-font-heavy"
                ref={el => {
                  HeaderRefs.current[index] = el;
                }}
              >
                {missionGridData.title}
              </h4>
              <div>
                <p
                  className="tw-text-base tw-font-roman tw-mb-5"
                  ref={el => {
                    DescriptionRefs.current[index] = el;
                  }}
                >
                  {missionGridData.description}
                </p>
                <a
                  className="tw-underline tw-text-white tw-text-base tw-font-roman"
                  href={missionGridData.link}
                  aria-label={missionGridData.ariaLabel}
                >
                  {missionGridData.label}
                </a>
              </div>
            </div>
          ))}
        </MissionGridSection>

        <MissionNav
          sectionClass="tw-bg-color-light-green"
          links={data.missionNavLinks}
          title="more about our mission"
        />

        <SlickCarousel {...data.moreToExplore} />
      </div>
    </Layout>
  );
}
